/**
 * MMM builder reference data and the two worked model outputs.
 *
 * PLACEHOLDER / ILLUSTRATIVE DATA. Classified INTERNAL.
 *
 * The cost model is stored as inputs only. Partnership Net 2.0 printed a cost
 * total that did not sum to its own line items; here every figure on the
 * estimate screen is computed by `src/domain/mmm` from these inputs, so the
 * total always reconciles with the list above it.
 */

import type {
  MmmChannelConfig,
  MmmCostModel,
  MmmOutput,
  MmmPartnership,
  MmmSourceOption,
} from '../../contracts';

export const MMM_PARTNERSHIPS: readonly MmmPartnership[] = [
  {
    id: 'P-001',
    partner: 'Manchester United',
    holder: 'Premier League',
    cluster: 'Football - Tier 1',
    countries: ['UK', 'Germany', 'France'],
    investment: 5200000,
    cur: 'GBP',
    start: '2021-01-01',
    end: '2024-12-31',
  },
  {
    id: 'P-002',
    partner: 'FC Bayern Munich',
    holder: 'Bundesliga',
    cluster: 'Football - Tier 1',
    countries: ['Germany', 'Austria'],
    investment: 4800000,
    cur: 'EUR',
    start: '2022-06-01',
    end: '2025-05-31',
  },
  {
    id: 'P-003',
    partner: 'NBA Global',
    holder: 'NBA',
    cluster: 'Basketball - Tier 1',
    countries: ['USA', 'UK', 'Germany'],
    investment: 3200000,
    cur: 'USD',
    start: '2021-09-01',
    end: '2024-08-31',
  },
  {
    id: 'P-004',
    partner: 'Formula 1',
    holder: 'F1 Group',
    cluster: 'Motorsport - Tier 1',
    countries: ['Global'],
    investment: 6500000,
    cur: 'USD',
    start: '2022-01-01',
    end: '2026-12-31',
  },
  {
    id: 'P-005',
    partner: 'Wimbledon',
    holder: 'AELTC',
    cluster: 'Tennis - Tier 2',
    countries: ['UK'],
    investment: 1800000,
    cur: 'GBP',
    start: '2020-01-01',
    end: '2024-12-31',
  },
];

export const MMM_CHANNELS: readonly MmmChannelConfig[] = [
  {
    id: 'tv',
    name: 'TV',
    source: 'tv-panel',
    metric: 'impressions',
    coverage: 'full',
    enabled: true,
  },
  {
    id: 'print',
    name: 'Print (Physical)',
    source: 'press-panel',
    metric: 'circulation',
    coverage: 'full',
    enabled: true,
  },
  {
    id: 'social-earned',
    name: 'Social Media Earned',
    source: 'social-listening',
    metric: 'engagements',
    coverage: 'partial',
    enabled: true,
  },
  {
    id: 'social-owned',
    name: 'Social Media Owned / Organic',
    source: 'social-suite',
    metric: 'impressions',
    coverage: 'full',
    enabled: true,
  },
  {
    id: 'social-shared',
    name: 'Social Media Shared',
    source: '',
    metric: '',
    coverage: 'none',
    enabled: false,
  },
  {
    id: 'social-paid',
    name: 'Social Media Paid',
    source: 'ads-manager',
    metric: 'impressions',
    coverage: 'partial',
    enabled: true,
  },
  {
    id: 'digital-pr',
    name: 'Digital PR / Editorial',
    source: 'media-monitoring',
    metric: 'reach',
    coverage: 'full',
    enabled: true,
  },
  {
    id: 'website-brand',
    name: 'Website (Brand-owned)',
    source: 'web-analytics',
    metric: 'visits',
    coverage: 'full',
    enabled: true,
  },
  {
    id: 'website-partner',
    name: 'Website (Partner site)',
    source: '',
    metric: '',
    coverage: 'none',
    enabled: false,
  },
  {
    id: 'search-partnership',
    name: 'Partnership Search',
    source: 'search-trends',
    metric: 'search-volume',
    coverage: 'full',
    enabled: true,
  },
  {
    id: 'search-partner',
    name: 'Partner Search',
    source: 'search-trends',
    metric: 'search-volume',
    coverage: 'full',
    enabled: true,
  },
  {
    id: 'stadium-inside',
    name: 'Stadium Inside (OOH)',
    source: 'venue-data',
    metric: 'attendance',
    coverage: 'partial',
    enabled: true,
  },
  {
    id: 'stadium-outside',
    name: 'Stadium Outside (OOH)',
    source: '',
    metric: '',
    coverage: 'none',
    enabled: false,
  },
  {
    id: 'media-ip',
    name: 'Media IP / Other',
    source: '',
    metric: '',
    coverage: 'none',
    enabled: false,
  },
];

export const MMM_SOURCES: readonly MmmSourceOption[] = [
  { id: 'tv-panel', label: 'TV audience panel' },
  { id: 'press-panel', label: 'Press panel' },
  { id: 'social-listening', label: 'Social listening' },
  { id: 'social-suite', label: 'Social suite' },
  { id: 'ads-manager', label: 'Ads manager' },
  { id: 'media-monitoring', label: 'Media monitoring' },
  { id: 'web-analytics', label: 'Web analytics' },
  { id: 'search-trends', label: 'Search trends' },
  { id: 'venue-data', label: 'Venue data' },
];

export const MMM_METRICS: readonly MmmSourceOption[] = [
  { id: 'impressions', label: 'Impressions' },
  { id: 'engagements', label: 'Engagements' },
  { id: 'visits', label: 'Visits' },
  { id: 'search-volume', label: 'Search Volume' },
  { id: 'circulation', label: 'Circulation' },
  { id: 'reach', label: 'Reach' },
  { id: 'attendance', label: 'Attendance' },
];

export const MMM_COST: MmmCostModel = {
  categoryOnboard: 15000, // per distinct sport category
  countryOnboard: 8000, // per distinct country
  baseFee: 45000, // data collection / acquisition / curation, per year
  perPartner: 6000,
  refreshDiscount: 0.35, // a refresh costs 65% of a new build
  addons: [
    { id: 'tv-audience', label: 'Add-on: TV audience data', cost: 20000 },
    { id: 'social-backfill', label: 'Add-on: Social media backfill', cost: 12000 },
  ],
  optimisations: [
    { id: 'opt-1', label: 'Client provides TV audience data', save: 20000 },
    { id: 'opt-2', label: 'Client provides social tracker dumps', save: 12000 },
    { id: 'opt-3', label: 'Track fewer channels for low-tier partners', save: 8000 },
  ],
};

export const MMM_OUTPUTS: readonly MmmOutput[] = [
  {
    id: 'allianz',
    title: 'Allianz Football Sponsorships MMM Build',
    sub: 'Football portfolio · updated Mar 12, 2026',
    portfolio: [
      {
        partner: 'UEFA Champions League',
        contribution: 178e6,
        roi: 4.12,
        efficiency: 94,
        direct: 125e6,
        halo: 53e6,
      },
      {
        partner: 'FC Bayern Munich',
        contribution: 156e6,
        roi: 4.45,
        efficiency: 96,
        direct: 109e6,
        halo: 47e6,
      },
      {
        partner: 'Manchester United',
        contribution: 142e6,
        roi: 3.67,
        efficiency: 91,
        direct: 99e6,
        halo: 43e6,
      },
    ],
    channels: [
      { channel: 'TV', spend: 198e6, impact: 834e6, efficiency: 4.21 },
      { channel: 'Digital', spend: 145e6, impact: 548e6, efficiency: 3.78 },
      { channel: 'OOH', spend: 87e6, impact: 313e6, efficiency: 3.6 },
      { channel: 'Print', spend: 28e6, impact: 81e6, efficiency: 2.89 },
      { channel: 'Radio', spend: 18e6, impact: 49e6, efficiency: 2.72 },
    ],
    countries: [
      { country: 'Germany', spend: 189e6, impact: 812e6, partnerships: 3 },
      { country: 'UK', spend: 143e6, impact: 578e6, partnerships: 3 },
      { country: 'France', spend: 76e6, impact: 289e6, partnerships: 2 },
      { country: 'Italy', spend: 68e6, impact: 246e6, partnerships: 2 },
    ],
    raw: [
      {
        category: 'Match Impressions',
        dataPoints: 15678,
        completeness: 100,
        sources: 'League feed, TV panel',
      },
      {
        category: 'Brand Tracking',
        dataPoints: 6234,
        completeness: 98,
        sources: 'Tracker A, Tracker B',
      },
      { category: 'Sales Data', dataPoints: 8945, completeness: 100, sources: 'Internal CRM' },
      {
        category: 'Media Spend',
        dataPoints: 7823,
        completeness: 99,
        sources: 'Finance, media agencies',
      },
      {
        category: 'Social Engagement',
        dataPoints: 12456,
        completeness: 95,
        sources: 'Social suite, listening',
      },
    ],
    cost: [
      { activity: 'Sports data acquisition', cost: 198000, hours: 890, complexity: 'High' },
      { activity: 'Data curation & normalisation', cost: 142000, hours: 760, complexity: 'High' },
      { activity: 'Channel mapping & QA', cost: 118000, hours: 640, complexity: 'Medium' },
      { activity: 'Country coverage onboarding', cost: 96000, hours: 480, complexity: 'Medium' },
      { activity: 'Documentation & methodology', cost: 78000, hours: 410, complexity: 'Low' },
      { activity: 'Agency handover & support', cost: 64000, hours: 190, complexity: 'Low' },
    ],
    heatRows: ['UEFA Champions League', 'FC Bayern Munich', 'Manchester United'],
    heatCols: ['Aug 2023', 'Oct 2023', 'Dec 2023', 'Feb 2024', 'Apr 2024', 'May 2024'],
    heat: [
      [94, 91, 96, 95, 93, 97],
      [92, 95, 97, 94, 96, 95],
      [88, 90, 93, 91, 89, 92],
    ],
    team: 8,
    weeks: 11,
  },
  {
    id: 'adidas',
    title: 'Adidas Sponsorship Portfolio MMM Dataset',
    sub: 'Analysis period 2022-2023 · updated Mar 10, 2026',
    portfolio: [
      {
        partner: 'FIFA World Cup',
        contribution: 412e6,
        roi: 4.85,
        efficiency: 97,
        direct: 295e6,
        halo: 117e6,
      },
      {
        partner: 'UEFA Champions League',
        contribution: 368e6,
        roi: 4.41,
        efficiency: 95,
        direct: 262e6,
        halo: 106e6,
      },
      {
        partner: 'Formula 1',
        contribution: 284e6,
        roi: 3.92,
        efficiency: 92,
        direct: 198e6,
        halo: 86e6,
      },
      { partner: 'NBA', contribution: 246e6, roi: 3.74, efficiency: 90, direct: 171e6, halo: 75e6 },
      {
        partner: 'Premier League',
        contribution: 218e6,
        roi: 3.61,
        efficiency: 89,
        direct: 154e6,
        halo: 64e6,
      },
      {
        partner: 'Marathon Majors',
        contribution: 162e6,
        roi: 3.28,
        efficiency: 86,
        direct: 112e6,
        halo: 50e6,
      },
      {
        partner: 'Olympic Programme',
        contribution: 148e6,
        roi: 3.11,
        efficiency: 84,
        direct: 101e6,
        halo: 47e6,
      },
      {
        partner: 'Tennis Grand Slams',
        contribution: 124e6,
        roi: 2.94,
        efficiency: 82,
        direct: 84e6,
        halo: 40e6,
      },
    ],
    channels: [
      { channel: 'Social Media', spend: 264e6, impact: 1120e6, efficiency: 4.24 },
      { channel: 'TV', spend: 238e6, impact: 962e6, efficiency: 4.04 },
      { channel: 'Digital', spend: 196e6, impact: 742e6, efficiency: 3.79 },
      { channel: 'OOH', spend: 118e6, impact: 412e6, efficiency: 3.49 },
      { channel: 'Print', spend: 42e6, impact: 126e6, efficiency: 3.0 },
      { channel: 'Radio', spend: 24e6, impact: 66e6, efficiency: 2.75 },
    ],
    countries: [
      { country: 'USA', spend: 246e6, impact: 1042e6, partnerships: 6 },
      { country: 'Germany', spend: 198e6, impact: 836e6, partnerships: 5 },
      { country: 'UK', spend: 164e6, impact: 672e6, partnerships: 5 },
      { country: 'China', spend: 142e6, impact: 548e6, partnerships: 4 },
      { country: 'Japan', spend: 86e6, impact: 322e6, partnerships: 3 },
      { country: 'Brazil', spend: 74e6, impact: 268e6, partnerships: 3 },
    ],
    raw: [
      {
        category: 'Match & Event Impressions',
        dataPoints: 24860,
        completeness: 100,
        sources: 'League feeds, TV panels',
      },
      {
        category: 'Brand Tracking',
        dataPoints: 9420,
        completeness: 96,
        sources: 'Tracker A, Tracker B',
      },
      {
        category: 'Sales Data',
        dataPoints: 14380,
        completeness: 99,
        sources: 'Internal CRM, retail EPOS',
      },
      {
        category: 'Media Spend',
        dataPoints: 11240,
        completeness: 98,
        sources: 'Finance, media agencies',
      },
      {
        category: 'Social Engagement',
        dataPoints: 18960,
        completeness: 94,
        sources: 'Social suite, listening',
      },
    ],
    cost: [
      { activity: 'Sports data acquisition', cost: 284000, hours: 1240, complexity: 'High' },
      { activity: 'Data curation & normalisation', cost: 212000, hours: 1080, complexity: 'High' },
      { activity: 'Channel mapping & QA', cost: 168000, hours: 880, complexity: 'Medium' },
      { activity: 'Country coverage onboarding', cost: 148000, hours: 720, complexity: 'Medium' },
      { activity: 'Documentation & methodology', cost: 96000, hours: 520, complexity: 'Low' },
      { activity: 'Agency handover & support', cost: 82000, hours: 260, complexity: 'Low' },
    ],
    heatRows: ['FIFA World Cup', 'UEFA Champions League', 'Formula 1', 'NBA'],
    heatCols: ['Q1 2022', 'Q2 2022', 'Q3 2022', 'Q4 2022', 'Q1 2023', 'Q2 2023'],
    heat: [
      [92, 88, 95, 97, 89, 93],
      [90, 91, 94, 96, 92, 94],
      [86, 89, 91, 93, 88, 90],
      [84, 87, 90, 92, 86, 89],
    ],
    team: 12,
    weeks: 16,
  },
];

export const MMM_AGENCIES: readonly string[] = [
  'Vendor A',
  'Vendor B',
  'Vendor C',
  'Vendor D',
  'Internal team',
  'Other',
];

export const MMM_PERIODS: readonly string[] = ['2021-2022', '2022-2023', '2023-2024', '2022-2024'];
